import {
  Children,
  isValidElement,
  useMemo,
  type ReactElement,
  type ReactNode,
} from 'react';

import { isSlot } from '../../../core/utils/is-slot.js';
import { SelectContent, type SelectContentProps } from '../SelectContent.js';
import { SelectCustomTrigger } from '../SelectCustomTrigger.js';
import { SelectDisplayValue } from '../SelectDisplayValue.js';
import {
  SelectEmptyState,
  type SelectEmptyStateProps,
} from '../SelectEmptyState.js';
import { SelectFilter, type SelectFilterProps } from '../SelectFilter.js';
import { SelectPrefix, type SelectPrefixProps } from '../SelectPrefix.js';
import { SelectTrigger, type SelectTriggerProps } from '../SelectTrigger.js';

interface SelectSlots {
  Content: ReactElement<SelectContentProps> | null;
  CustomTrigger: ReactElement<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    { [key: string]: any } | null | undefined
  > | null;
  DisplayValue: ReactNode[] | null;
  Trigger: ReactElement<SelectTriggerProps> | null;
  Prefix: ReactElement<SelectPrefixProps> | null;
  Filter: ReactElement<SelectFilterProps> | null;
  EmptyState: ReactElement<SelectEmptyStateProps> | null;
}

/**
 * Helper hook that will extract the Slots used in the select and collect the data / components.
 *
 * @internal
 */
export function useSelectSlots(children: ReactNode): SelectSlots {
  return useMemo(() => {
    const slots: SelectSlots = {
      Content: null,
      CustomTrigger: null,
      DisplayValue: null,
      Trigger: null,
      Prefix: null,
      Filter: null,
      EmptyState: null,
    };

    Children.forEach(children, (child) => {
      if (isSlot(child, SelectContent)) {
        slots.Content = child;
        // DISCUSS: Is this an issue when there are a lot of select options?
        Children.forEach(child.props.children, (child) => {
          if (isSlot(child, SelectFilter)) {
            slots.Filter = child;
            return;
          } else if (isSlot(child, SelectEmptyState)) {
            slots.EmptyState = child;
          }
        });
        return;
      } else if (isSlot(child, SelectTrigger)) {
        slots.Trigger = child;

        Children.forEach(child.props.children, (child) => {
          if (isSlot(child, SelectDisplayValue)) {
            Children.forEach(child.props.children, (child) => {
              if (isSlot(child, SelectPrefix)) {
                slots.Prefix = child;
                return;
              }

              // Concatenate free form content in the
              slots.DisplayValue = slots.DisplayValue || [];
              slots.DisplayValue.push(child);
            });
          }
        });
      } else if (isSlot(child, SelectCustomTrigger)) {
        // The custom trigger should only use the first child.
        // TODO: What about fragments etc?
        const triggerValue = Children.toArray(child.props.children)[0];
        if (isValidElement<SelectSlots['CustomTrigger']>(triggerValue)) {
          slots.CustomTrigger = triggerValue;
        }
      } else if (isSlot(child, SelectFilter)) {
        slots.Filter = child;
      }
    });

    return slots;
  }, [children]);
}
